import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnnouncementViewModal, LoadingSpinner, ShiftAllocationCard, useAuth } from '../components'
import { Button, Card } from '../components/ui'
import { AnnouncementMeta, AnnouncementViewModalMeta, API_BASE_URL } from '../data'
import { Allocation, Announcement, HomeMetaProps, Training } from '../data/types'
import { useShowShiftInfo, useVisitAnnounced } from '../hooks/useGlobalSetting'
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, WarningIcon } from '../icons'

export const Home = ({ meta }: { meta: HomeMetaProps }) => {
    const { greeting } = meta
    const { user, incompletePoliciesCount, incompletePolicies } = useAuth()
    const { value: visitAnnounced, loading: loadingVisitAnnounced } = useVisitAnnounced()
    const { value: showShiftInfo, loading: loadingShowShiftInfo } = useShowShiftInfo()
    const [allAnnouncements, setAllAnnouncements] = useState<Announcement[]>([])
    const [loadingAnnouncements, setLoadingAnnouncements] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
    const [isAnnouncementModalOpen, setIsAnnouncementModalOpen] = useState(false);
    const [volunteerTrainings, setVolunteerTrainings] = useState<Training[]>([])
    const [loadingTrainings, setLoadingTrainings] = useState(true)
    const [volunteerAllocations, setVolunteerAllocations] = useState<Allocation[]>([])
    const [loadingAllocations, setLoadingAllocations] = useState(true)

    const [page, setPage] = useState(1)
    const pageSize = 4


    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoadingAnnouncements(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/announcement`, { credentials: 'include' })
                if (!response.ok) {
                    const text = await response.text()
                    const textObj = JSON.parse(text)
                    throw new Error(`${textObj.message}`)
                }
                const result = await response.json()
                setAllAnnouncements(result)
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching duties')
            }
            finally {
                setLoadingAnnouncements(false)
            }
        }

        const fetchTrainings = async () => {
            if (!user?.volunteer_csid) {
                setLoadingTrainings(false)
                return
            }

            setLoadingTrainings(true)

            try {
                const response = await fetch(`${API_BASE_URL}/volunteer/${user.volunteer_csid}/training`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`)
                }
                const result = await response.json()
                setVolunteerTrainings(result)
            }
            catch (err) {
                // Silently fail for trainings - not critical for home page
                console.error('Error fetching trainings:', err)
            }
            finally {
                setLoadingTrainings(false)
            }
        }

        const fetchAllocations = async () => {
            if (!user?.volunteer_csid) {
                setLoadingAllocations(false)
                return
            }

            setLoadingAllocations(true)

            try {
                // Always filter by visibility on home page (personal view), even for admins
                const response = await fetch(`${API_BASE_URL}/volunteer/${user.volunteer_csid}/allocation?filter_visibility=true`, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`)
                }
                const result = await response.json()
                setVolunteerAllocations(result)
            }
            catch (err) {
                // Silently fail for allocations - not critical for home page
                console.error('Error fetching allocations:', err)
            }
            finally {
                setLoadingAllocations(false)
            }
        }

        fetchAnnouncements()
        fetchTrainings()
        fetchAllocations()

    }, [user?.volunteer_csid])


    const handleAnnouncementClick = (announcementData: Announcement) => {
        setSelectedAnnouncement(announcementData)
        setIsAnnouncementModalOpen(true)
    }

    const handleClose = () => {
        setSelectedAnnouncement(null)
        setIsAnnouncementModalOpen(false)
    }

    console.log("user", user);


    return (
        <div className="flex-1 container mx-auto px-4 py-8">
            <p className="text-sm font-bold text-gray-900 dark:text-white">{typeof greeting === 'function' ? greeting(user.volunteer?.first_name) : greeting}</p>
            <p className="mt-2 text-gray-700 dark:text-gray-300">{meta.motd}</p>

            {
                (
                    // Check if volunteer is from a non-local country
                    user?.volunteer?.country && !['austria', 'cyprus', 'denmark', 'estonia', 'finland', 'germany', 'hungary', 'italy', 'netherlands', 'north macedonia', 'norway', 'poland', 'sweden', 'slovenia', 'lithuania', 'greece'].some(c => user?.volunteer?.country?.toLowerCase().includes(c))
                )
                && (
                    <div className='flex items-start gap-3 mt-4'>
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <span className="text-primary-600 dark:text-primary-400 font-bold text-sm">!</span>
                        </span>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {meta.travelInfo}
                        </p>
                    </div>
                )
            }

            {/* Profile Incomplete Warning */}
            {user?.volunteer && !user.volunteer.profile_submitted_at && (
                <div className="mt-6 bg-warn-50 dark:bg-warn-900/20 border-s-4 border-warn-500 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <WarningIcon className="w-6 h-6 text-warn-600 dark:text-warn-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="font-semibold text-warn-800 dark:text-warn-200 mb-1">
                                {visitAnnounced ? meta.visitAnnouncedProfileTitle : meta.profileIncompleteWarningTitle}
                            </h3>
                            <p className="text-sm text-warn-700 dark:text-warn-300 mb-3">
                                {visitAnnounced ? meta.visitAnnouncedProfileMessage : meta.profileIncompleteWarningMessage}
                            </p>
                            <Link
                                to="/profile"
                                state={{ openEditModal: true }}
                                className="inline-flex items-center px-4 py-2 bg-warn-600 hover:bg-warn-700 dark:bg-warn-500 dark:hover:bg-warn-600 text-white font-medium rounded-lg transition-colors duration-200 text-sm animate-pulse"
                            >
                                {visitAnnounced ? meta.visitAnnouncedCompleteButton : meta.completeProfileButton}
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Incomplete Policies Warning */}
            {user?.volunteer_csid && incompletePoliciesCount > 0 && (
                <div className="mt-6 bg-danger-50 dark:bg-danger-900/20 border-s-4 border-danger-500 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <WarningIcon className="w-6 h-6 text-danger-600 dark:text-danger-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="font-semibold text-danger-800 dark:text-danger-200 mb-1">
                                {meta.policyIncompleteWarningTitle}
                            </h3>
                            <p className="text-sm text-danger-700 dark:text-danger-300 mb-3">
                                {meta.policyIncompleteWarningMessage(incompletePoliciesCount)}
                            </p>
                            <div className="space-y-2">
                                {incompletePolicies.map((policy) => (
                                    <Link
                                        key={policy.id}
                                        to={`/volunteer/training/${policy.id}/details`}
                                        className="inline-flex items-center px-4 py-2 bg-danger-600 hover:bg-danger-700 dark:bg-danger-500 dark:hover:bg-danger-600 text-white font-medium rounded-lg transition-colors duration-200 text-sm me-2 mb-2 animate-pulse"
                                    >
                                        {policy.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {loadingAnnouncements && (
                <div className="mt-8">
                    <LoadingSpinner meta={{ loadingText: 'Loading announcements...' }} />
                </div>
            )}

            {error && (
                <div className="mt-8 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-danger-800 dark:text-danger-200">
                        <WarningIcon className="w-5 h-5" />
                        <span className="font-semibold">{AnnouncementMeta.errorTitle}</span>
                    </div>
                    <p className="text-sm text-danger-700 dark:text-danger-300 mt-1">{error}</p>
                </div>
            )}


            {
                !loadingAnnouncements && !error && (
                    <div className="mt-8">
                        {/* Volunteer Messages Section Title */}
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.volunteerMessagesTitle}
                        </h2>


                        {/* Volunteer Messages Cards */}
                        <div className="grid gap-2 md:gap-4">
                            {allAnnouncements
                                .slice((page - 1) * pageSize, page * pageSize)
                                .map((announcement) => (
                                    <Card
                                        key={announcement.id}
                                        className="hover:shadow-lg transition-shadow cursor-pointer"
                                        onClick={() => handleAnnouncementClick(announcement)}
                                    >
                                        <div className="p-3 sm:p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                {/* Priority Icon + Title */}
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    {announcement.priority ? (
                                                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center" title={meta.priorityLabel}>
                                                            <span className="text-primary-600 dark:text-primary-400 font-bold text-sm">!</span>
                                                        </span>
                                                    ) : null}
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                            {announcement.title}
                                                        </h3>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                                            {new Date(announcement.created_at).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* View Button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleAnnouncementClick(announcement)
                                                    }}
                                                    className="flex-shrink-0 p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                                                    title={meta.viewButton}
                                                >
                                                    <EyeIcon />
                                                </button>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                        </div>

                        {/* Pagination */}
                        <div className="flex justify-end gap-2 items-center mt-6">
                            <Button
                                variant='outline'
                                color='neutral'
                                size='sm'
                                disabled={page === 1}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                <ChevronLeftIcon className="w-4 h-4" /> {meta.previousButton}
                            </Button>

                            <span className="text-gray-600 dark:text-gray-300">
                                {meta.pageLabel} {page} of {Math.ceil(allAnnouncements.length / pageSize)}
                            </span>

                            <Button
                                variant='outline'
                                color='neutral'
                                size='sm'
                                disabled={page >= Math.ceil(allAnnouncements.length / pageSize)}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                {meta.nextButton} <ChevronRightIcon className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>)
            }

            {/* Assigned Trainings Section */}
            {
                !loadingTrainings && user?.volunteer_csid && (
                    <div className="mt-8">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.assignedTrainingsTitle}
                        </h2>

                        <Card className="hover:shadow-lg transition-shadow">
                            <div className="p-4">
                                {(() => {
                                    const incompleteTrainings = volunteerTrainings.filter(
                                        t => t.completion_status === 0 || t.completion_status === null
                                    )
                                    const incompleteCount = incompleteTrainings.length

                                    return (
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex-1">
                                                {incompleteCount === 0 ? (
                                                    <p className="text-success-700 dark:text-success-300 font-medium">
                                                        {meta.noIncompleteTrainingsMessage}
                                                    </p>
                                                ) : (
                                                    <p className="text-gray-700 dark:text-gray-300">
                                                        {meta.incompleteTrainingsMessage(incompleteCount)}
                                                    </p>
                                                )}
                                            </div>
                                            <Link to="/volunteer/training">
                                                <Button
                                                    variant="outline"
                                                    color="primary"
                                                    size="md"
                                                >
                                                    {meta.viewTrainingsButton}
                                                </Button>
                                            </Link>
                                        </div>
                                    )
                                })()}
                            </div>
                        </Card>
                    </div>
                )
            }

            {/* Assigned Shifts Section */}
            {
                loadingAllocations && user?.volunteer_csid && visitAnnounced && (
                    <div className="mt-8">
                        <LoadingSpinner meta={{ loadingText: meta.loadingShiftsText }} />
                    </div>
                )
            }

            {
                !loadingAllocations && user?.volunteer_csid && visitAnnounced && (
                    <div className="mt-8">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.assignedShiftsTitle}
                        </h2>

                        {(!showShiftInfo || volunteerAllocations.length === 0) ? (
                            <Card className="hover:shadow-lg transition-shadow">
                                <div className="p-4">
                                    <p className="text-gray-600 dark:text-gray-400 text-center">
                                        {meta.dutiesNotYetAssignedMessage}
                                    </p>
                                </div>
                            </Card>
                        ) : (
                            <div className="grid gap-4">
                                {volunteerAllocations.map((allocation) => (
                                        <ShiftAllocationCard
                                            key={allocation.id}
                                            allocation={allocation}
                                            meta={{
                                                areaLabel: meta.areaLabel,
                                                shiftLeadLabel: meta.shiftLeadLabel,
                                                shiftLabel: meta.shiftLabel,
                                            }}
                                        />
                                    ))}
                            </div>
                        )}
                    </div>
                )
            }

            <AnnouncementViewModal
                meta={AnnouncementViewModalMeta}
                isOpen={isAnnouncementModalOpen}
                onClose={handleClose}
                initialAnnouncement={selectedAnnouncement}
            />

        </div >
    )
}
